import React, { useRef, useCallback, useState } from 'react';

/**
 * Simple absolute-positioned canvas for strategy nodes.
 * Props:
 *  nodes: [{id,name,x,y, defaultOutput, outputs}]
 *  onMove: (id, x, y) => void
 *  selectedId
 *  onSelect: (id)=>void
 *  activeComparison: { lhsRef, rhsRef?, rhsKind }
 */
export default function GraphCanvas({ nodes, onMove, selectedId, onSelect, activeComparison, validation }) {
  const containerRef = useRef(null);
  const [dragState, setDragState] = useState(null); // {id,dx,dy}

  const parseRef = useCallback((ref)=>{
    if (!ref) return null;
    if (ref.includes(':')) { const [nid] = ref.split(':'); return nid; }
    return ref;
  }, []);

  const highlightedIds = new Set();
  if (activeComparison) {
    const l = parseRef(activeComparison.lhsRef); if (l) highlightedIds.add(l);
    if (activeComparison.rhsKind === 'ref') {
      const r = parseRef(activeComparison.rhsRef); if (r) highlightedIds.add(r);
    }
  }

  const handlePointerDown = (e, id) => {
    const node = nodes.find(n=>n.id===id);
    if (!node) return;
    const rect = containerRef.current?.getBoundingClientRect();
    const startX = e.clientX - (rect?.left||0);
    const startY = e.clientY - (rect?.top||0);
    setDragState({ id, offsetX: startX - (node.x||0), offsetY: startY - (node.y||0) });
    onSelect(id);
  };

  const handlePointerMove = (e) => {
    if (!dragState) return;
    const rect = containerRef.current?.getBoundingClientRect();
    const curX = e.clientX - (rect?.left||0);
    const curY = e.clientY - (rect?.top||0);
    const x = curX - dragState.offsetX;
    const y = curY - dragState.offsetY;
    onMove(dragState.id, x, y);
  };

  const handlePointerUp = () => setDragState(null);

  // Build quick error/warning lookup
  const nodeIssues = new Map();
  if (validation) {
    [...validation.errors, ...validation.warnings].forEach(issue => {
      if (issue.type !== 'node') return;
      const arr = nodeIssues.get(issue.id) || [];
      arr.push(issue);
      nodeIssues.set(issue.id, arr);
    });
  }

  // Edges from active comparison only for now (minimal useful implementation)
  const edges = [];
  if (activeComparison) {
    const lhs = parseRef(activeComparison.lhsRef);
    if (lhs) edges.push({ from: lhs, to: activeComparison.id ? `cmp-${activeComparison.id}` : 'cmp-active' });
    if (activeComparison.rhsKind === 'ref') {
      const rhs = parseRef(activeComparison.rhsRef);
      if (rhs) edges.push({ from: rhs, to: activeComparison.id ? `cmp-${activeComparison.id}` : 'cmp-active' });
    }
  }

  // Comparison virtual node position: center top
  const cmpVirtual = activeComparison ? { id: activeComparison.id ? `cmp-${activeComparison.id}` : 'cmp-active', x: 10, y: 10 } : null;

  const getNodeCenter = (id) => {
    if (cmpVirtual && cmpVirtual.id === id) return { x: cmpVirtual.x + 50, y: cmpVirtual.y + 16 };
    const n = nodes.find(n=>n.id===id);
    if (!n) return { x:0, y:0 };
    return { x: (n.x||0) + 60, y: (n.y||0) + 24 };
  };

  const renderedEdges = edges.map((e,i)=>{
    const from = getNodeCenter(e.from);
    const to = getNodeCenter(e.to);
    return <line key={i} x1={from.x} y1={from.y} x2={to.x} y2={to.y} stroke="#888" strokeWidth={2} markerEnd="url(#arrow)" />;
  });

  return (
    <div
      ref={containerRef}
      className="relative w-full h-80 border rounded-md bg-gradient-to-br from-background via-muted/30 to-background overflow-hidden"
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerUp}
    >
      <svg className="absolute inset-0 w-full h-full pointer-events-none">
        <defs>
          <marker id="arrow" markerWidth="10" markerHeight="10" refX="6" refY="3" orient="auto" markerUnits="strokeWidth">
            <path d="M0,0 L0,6 L6,3 z" fill="#888" />
          </marker>
        </defs>
        {renderedEdges}
      </svg>
      {cmpVirtual && (
        <div
          className="absolute text-[10px] px-2 py-1 rounded bg-purple-500/20 border border-purple-500 select-none"
          style={{ left: cmpVirtual.x, top: cmpVirtual.y }}
        >cmp:{activeComparison.id}</div>
      )}
      {nodes.map(n => {
        const active = n.id === selectedId;
        const highlight = highlightedIds.has(n.id);
        const issues = nodeIssues.get(n.id) || [];
        const hasError = issues.some(i=>validation?.errors.includes(i));
        const hasWarning = !hasError && issues.length > 0;
        return (
          <div
            key={n.id}
            role="button"
            tabIndex={0}
            onPointerDown={(e)=>handlePointerDown(e, n.id)}
            onDoubleClick={()=>onSelect(n.id)}
            className={`absolute select-none text-[10px] px-2 py-1 rounded shadow-sm border cursor-move transition-colors
              ${active ? 'border-primary bg-primary/20' : highlight ? 'border-amber-500 bg-amber-300/20' : hasError ? 'border-red-500 bg-red-400/20' : hasWarning ? 'border-amber-500 bg-amber-400/20' : 'border-borderDefault bg-card/90 hover:bg-card'}
            `}
            style={{ left: (n.x||0), top: (n.y||0), minWidth: 90 }}
          >
            <div className="font-semibold truncate flex items-center gap-1">
              {n.id}
              {hasError && <span className="w-2 h-2 rounded-full bg-red-500" title="Error" />}
              {hasWarning && <span className="w-2 h-2 rounded-full bg-amber-500" title="Warning" />}
            </div>
            <div className="truncate opacity-70">{n.name}</div>
            {Array.isArray(n.outputs) && n.outputs.length > 1 && (
              <div className="mt-0.5 flex flex-wrap gap-0.5">
                {n.outputs.map(o => (
                  <span key={o} className={`px-1 rounded border text-[9px] ${n.defaultOutput===o? 'bg-primary/40 border-primary' : 'bg-muted/40'}`}>{o}</span>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
